import { useContext, useEffect, useState } from 'react';
import {
    LiabilityItemView,
    ListPropertyServicesController,
} from 'fragments/property-card/list-property-services/interfaces';
import { Form } from 'antd';
import { TypeServicesService, useAPITypeServicesService } from 'services/typeServices/type-services.service';
import { TypeServiceDto } from 'services/typeServices/type-services.dto';
import { PropertyContext } from 'fragments/property-card/property.context';
import {
    LiabilitiesService,
    LiabilityDto,
    LiabilityInstance,
    useAPILiabilitiesService,
} from 'services/liabilities/liabilities.service';
import {
    EditLiabilityFormInputs,
    SelectableTypeServiceOptions,
} from 'fragments/property-card/create-edit-liability/interfaces';
import { mapLiabilityToEditFormInputs } from 'services/liabilities/liabilities.mapper';
import { LabelValue } from 'components/view-models';

export const useListPropertyServicesController = (
    executeOnCancelProp?: () => void,
    liabilitiesService: LiabilitiesService = useAPILiabilitiesService(),
    typeServicesService: TypeServicesService = useAPITypeServicesService(),
): ListPropertyServicesController => {
    /* State */
    const [isEditSingleServiceVisible, setIsEditSingleServiceVisible] = useState(false);
    const [isCreateNewServiceVisible, setIsCreateNewServiceVisible] = useState(false);
    const [formInitialValues, setFormInitialValues] = useState<EditLiabilityFormInputs>();
    const [typeServices, setTypeServices] = useState<SelectableTypeServiceOptions[]>([]);
    const [editInstance, setEditInstance] = useState<LiabilityInstance>(LiabilityInstance.PROPERTY_LIABILITY);
    const { property, setProperty } = useContext(PropertyContext);
    const [formInstance] = Form.useForm();

    /* Listeners */
    useEffect(() => {
        fetchLiabilities();
        fetchTypeServices();
    }, []);

    /* View Events */

    const onClickEdit = (liabilityId: number) => {
        fillEditForm(liabilityId).catch(() => {
            // TODO: Show error message
        });
    };

    const fetchLiabilities = () => {
        if (!property.id) return;
        liabilitiesService
            .getAllLiabilitiesByPropertyId(property.id)
            .then((allLiabilities) => {
                setProperty((prevstate) => {
                    return { ...prevstate, services: allLiabilities };
                });
            })
            .catch(() => {
                // TODO: Show erro message
            });
    };

    const fillEditForm = async (liabilityId: number) => {
        const liability = await liabilitiesService.getLiability(liabilityId);
        const initialValues = mapLiabilityToEditFormInputs(liability);

        if (!initialValues || !liability.liabilityType) {
            return;
        }

        setEditInstance(liability.liabilityType);
        setFormInitialValues(initialValues);
        setIsEditSingleServiceVisible(true);
    };

    const onCancel = () => {
        cleanFormAndHide();
        if (executeOnCancelProp) executeOnCancelProp();
    };

    const onCreateEditBack = () => {
        cleanFormAndHide();
    };

    const cleanFormAndHide = () => {
        formInstance.resetFields();
        setIsCreateNewServiceVisible(false);
        setIsEditSingleServiceVisible(false);
    };

    const onClickDelete = (liabilityId: number) => {
        liabilitiesService
            .deleteLiability(liabilityId)
            .then((successful) => {
                if (successful) {
                    setProperty((prevValue) => {
                        const filteredPropLiabilities = prevValue.services?.filter((s) => s.id != liabilityId);
                        return { ...prevValue, services: filteredPropLiabilities };
                    });

                    setTypeServices((prevValue) => {
                        console.log(prevValue);
                        return prevValue.filter((o) => o.id != liabilityId);
                    });
                } else {
                    // TODO: Show error message
                }
            })
            .catch(() => {
                // TODO: Show error message
            });
    };

    const onClickCreate = () => {
        setIsCreateNewServiceVisible(true);
    };

    /* Private Methods */
    const fetchTypeServices = () => {
        typeServicesService.getAllTypeServices().then((dtos) => {
            setTypeServices(dtos.map(mapDtoToSelectableTypeServicesOptions));
        });
    };

    const mapDtoToSelectableTypeServicesOptions = (dto: TypeServiceDto): SelectableTypeServiceOptions => {
        return {
            id: dto.id,
            typeServiceTitle: dto.title,
        };
    };

    const onFormSubmit = () => {
        formInstance.submit();
        setIsEditSingleServiceVisible(false);
        setIsCreateNewServiceVisible(false);
    };

    const mapDtoToItemView = (dto: LiabilityDto): LiabilityItemView => {
        let subtitle;
        if (dto.contractId) {
            subtitle = `Aplica desde ${dto.fromMonth}/${dto.fromYear} hasta ${dto.toMonth}/${dto.toYear}`;
        }
        if (dto.followUpId) {
            subtitle = `Aplica solo a este mes`;
        }
        return { id: dto.id ?? 0, title: dto.name, subtitle };
    };

    // Return state and events
    return {
        isEditSingleServiceVisible,
        form: formInstance,
        editInstance,
        liabilities: property.services?.map(mapDtoToItemView),
        typeServiceList: typeServices,
        isCreateNewServiceVisible,
        formInitialValues,
        propertyId: property.id ?? 0,
        onClickEdit,
        onClickDelete,
        onFormSubmit,
        onClickCreate,
        onCancel,
        onCreateEditBack,
    };
};
